//rtuService.ts - Updated to use centralized API configuration

import {
  Action,
  ActionLaunchRequestStatus,
  ActionTargetReference,
  DeploymentCompatibilityPreview,
  DeploymentCompatibilityTarget,
  PageResponseDto,
} from "../types/actionTypes";
import apiClient from "./apiClient";
import { API_CONFIG, API_ENDPOINTS } from "../config/apiConfig";
import { createKeyedInFlightShare } from "../utils/shareInFlightRequest";
import { RtuProbeResponseDto } from "../types/RtuProbe";
import { InstantSystemEvent } from "../types/RtuData";
import type { RtuClusterDetails } from "../types/RTU";
import {
  RtuBulkPingRunRequestDto,
  RtuBulkPingRunResponseDto,
  RtuPingBatchSnapshotDto,
  RtuPingRunRequestDto,
  RtuPingRunResponseDto,
} from "../types/RtuPing";

export async function createAction(
  ips: string[]
): Promise<Action> {
  try {
    const params = new URLSearchParams();
    ips.forEach((ip) => params.append("ips", ip));

    const response = await apiClient.post(
      `${API_ENDPOINTS.ACTIONS.CREATE}?${params.toString()}`
    );
    return response.data;
  } catch (error: any) {
    handleAxiosError(error);
    throw error;
  }
}

export async function rebootRTU(
  actionId: number,
  optionalParams: { [key: string]: string | number } = {}
): Promise<Action> {
  try {
    const response = await apiClient.post(
      API_ENDPOINTS.ACTIONS.START_REBOOT,
      null,
      {
        params: { actionId, ...optionalParams },
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    return response.data;
  } catch (error: any) {
    handleAxiosError(error);
    throw error;
  }
}

export interface AtomicLaunchOptions {
  comment?: string;
  idempotencyKey: string;
}

export interface UploadAtomicLaunchOptions extends AtomicLaunchOptions {
  cfg_preserve?: string | number;
  comment?: string;
  data_preserve?: string | number;
  onUploadProgress?: (percent: number) => void;
  run?: string | number;
}

const atomicHeaders = (idempotencyKey: string, headers: Record<string, string> = {}) => ({
  ...headers,
  'Idempotency-Key': idempotencyKey,
});

const binaryOption = (value: string | number | undefined): boolean | undefined => {
  if (value === undefined || value === null || value === '') return undefined;
  return String(value) === '1' || String(value).toLowerCase() === 'true';
};

const appendJsonRequestPart = (formData: FormData, request: Record<string, unknown>) => {
  formData.append(
    'request',
    new Blob([JSON.stringify(request)], { type: 'application/json' }),
  );
};

export const createActionIdempotencyKey = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `dms-action-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

export const isUnknownLaunchOutcome = (error: unknown): boolean => {
  const axiosError = error as { code?: string; response?: { status?: number }; message?: string };
  const status = axiosError?.response?.status;
  // A proxy or server timeout can be returned after the launch transaction
  // committed. Reconcile with the same idempotency key before allowing retry.
  if (status !== undefined) {
    return status >= 500 || [408, 425, 429].includes(status);
  }
  return ['ECONNABORTED', 'ETIMEDOUT', 'ERR_NETWORK'].includes(String(axiosError?.code ?? ''))
    || /timeout|network error|failed to fetch/i.test(String(axiosError?.message ?? ''));
};

/** A stale browser target must be refreshed, never retried with the same pair. */
export const isRtuTargetIdentityChangedError = (error: unknown): boolean => {
  const response = (error as { response?: { status?: number; data?: { code?: string; errorCode?: string } } })?.response;
  const code = response?.data?.code ?? response?.data?.errorCode;
  return response?.status === 409 && code === 'RTU_TARGET_IDENTITY_CHANGED';
};

export async function fetchActionLaunchRequest(idempotencyKey: string): Promise<ActionLaunchRequestStatus> {
  const response = await apiClient.get(API_ENDPOINTS.ACTIONS.LAUNCH_REQUEST_STATUS(idempotencyKey));
  return response.data as ActionLaunchRequestStatus;
}

export async function previewDeploymentCompatibility(
  targets: ActionTargetReference[],
  fileName: string,
  signal?: AbortSignal,
): Promise<DeploymentCompatibilityPreview> {
  const response = await apiClient.post(
    API_ENDPOINTS.ACTIONS.DEPLOYMENT_COMPATIBILITY_PREVIEW,
    { targets, fileName },
    { signal },
  );
  const payload = response.data as Partial<DeploymentCompatibilityPreview>;
  return {
    ...payload,
    targets: Array.isArray(payload.targets)
      ? payload.targets.map((target: DeploymentCompatibilityTarget) => ({
          ...target,
          status: target.status ?? target.result ?? 'UNVERIFIABLE',
        }))
      : [],
  } as DeploymentCompatibilityPreview;
}

export async function launchRebootRtus(
  targets: ActionTargetReference[],
  optionalParams: { [key: string]: string | number } & AtomicLaunchOptions
): Promise<Action> {
  try {
    const response = await apiClient.post(
      API_ENDPOINTS.ACTIONS.LAUNCH_REBOOT,
      {
        targets,
        value: optionalParams.val,
        comment: optionalParams.comment ?? '',
      },
      {
        headers: atomicHeaders(optionalParams.idempotencyKey),
      }
    );
    return response.data;
  } catch (error: any) {
    handleAxiosError(error);
    throw error;
  }
}

export async function launchRtuInfoRtus(
  targets: ActionTargetReference[],
  optionalParams: { [key: string]: string | number } & AtomicLaunchOptions
): Promise<Action> {
  try {
    const response = await apiClient.post(
      API_ENDPOINTS.ACTIONS.LAUNCH_RTU_INFO,
      { targets, comment: optionalParams.comment ?? '' },
      {
        headers: atomicHeaders(optionalParams.idempotencyKey),
      }
    );
    return response.data;
  } catch (error: any) {
    handleAxiosError(error);
    throw error;
  }
}

interface DownloadLaunchOptions extends AtomicLaunchOptions {}

export async function launchRtuDebugLogDownload(
  targets: ActionTargetReference[],
  options: DownloadLaunchOptions
): Promise<Action> {
  try {
    const response = await apiClient.post(
      API_ENDPOINTS.ACTIONS.LAUNCH_DEBUG_LOG_DOWNLOAD,
      { targets, comment: options.comment ?? '' },
      { headers: atomicHeaders(options.idempotencyKey) }
    );
    return response.data;
  } catch (error: any) {
    handleAxiosError(error);
    throw error;
  }
}

export async function launchConfigDownload(
  targets: ActionTargetReference[],
  options: DownloadLaunchOptions & { selection: 'CURRENT' | 'EXISTING'; save?: boolean; filename?: string }
): Promise<Action> {
  try {
    const response = await apiClient.post(
      API_ENDPOINTS.ACTIONS.LAUNCH_CONFIG_DOWNLOAD,
      {
        targets,
        selection: options.selection,
        ...(options.selection === 'EXISTING' ? { filename: options.filename?.trim() } : { save: Boolean(options.save) }),
        comment: options.comment ?? '',
      },
      { headers: atomicHeaders(options.idempotencyKey) }
    );
    return response.data;
  } catch (error: any) {
    handleAxiosError(error);
    throw error;
  }
}

export const launchCurrentConfigDownload = (
  targets: ActionTargetReference[],
  options: DownloadLaunchOptions & { save: boolean }
): Promise<Action> => launchConfigDownload(targets, { ...options, selection: 'CURRENT' });

export async function launchDeploymentDownload(
  targets: ActionTargetReference[],
  options: DownloadLaunchOptions & { selection: 'LATEST' | 'EXISTING'; filename?: string }
): Promise<Action> {
  try {
    const response = await apiClient.post(
      API_ENDPOINTS.ACTIONS.LAUNCH_DEPLOY_DOWNLOAD,
      {
        targets,
        selection: options.selection,
        ...(options.selection === 'EXISTING' ? { filename: options.filename?.trim() } : {}),
        comment: options.comment ?? '',
      },
      { headers: atomicHeaders(options.idempotencyKey) }
    );
    return response.data;
  } catch (error: any) {
    handleAxiosError(error);
    throw error;
  }
}

export interface RtuFileList {
  fileType: string;
  total: number;
  files: string[];
}

export async function fetchRtuConfigurationFiles(
  targets: ActionTargetReference[],
  signal?: AbortSignal,
): Promise<RtuFileList> {
  try {
    const response = await apiClient.post(
      API_ENDPOINTS.ACTIONS.RTU_CONFIGURATION_FILES,
      { targets },
      { signal, timeout: API_CONFIG.RTU_FILE_LIST_TIMEOUT }
    );
    return response.data;
  } catch (error: any) {
    handleAxiosError(error);
    throw error;
  }
}

export async function fetchRtuDeploymentFiles(
  targets: ActionTargetReference[],
  signal?: AbortSignal,
): Promise<RtuFileList> {
  try {
    const response = await apiClient.post(
      API_ENDPOINTS.ACTIONS.RTU_DEPLOYMENT_FILES,
      { targets },
      { signal, timeout: API_CONFIG.RTU_FILE_LIST_TIMEOUT }
    );
    return response.data;
  } catch (error: any) {
    handleAxiosError(error);
    throw error;
  }
}

export async function launchUploadConfig(
  file: File,
  targets: ActionTargetReference[],
  optionalParams: UploadAtomicLaunchOptions
): Promise<Action> {
  const formData = new FormData();
  formData.append('file', file);
  appendJsonRequestPart(formData, {
    targets,
    run: binaryOption(optionalParams.run),
    comment: optionalParams.comment ?? '',
  });

  try {
    const response = await apiClient.post(
      API_ENDPOINTS.ACTIONS.LAUNCH_CONFIG_UPDATE,
      formData,
      {
        // The browser supplies the multipart boundary. Setting Content-Type
        // manually can produce malformed uploads in some Axios/browser versions.
        headers: atomicHeaders(optionalParams.idempotencyKey),
        timeout: API_CONFIG.UPLOAD_TIMEOUT,
        onUploadProgress: (event) => {
          if (!optionalParams.onUploadProgress || !event.total) return;
          optionalParams.onUploadProgress(Math.min(100, Math.round((event.loaded / event.total) * 100)));
        },
      }
    );
    return response.data;
  } catch (error: any) {
    handleAxiosError(error);
    throw error;
  }
}

export async function launchUploadDeployment(
  file: File,
  targets: ActionTargetReference[],
  optionalParams: UploadAtomicLaunchOptions
): Promise<Action> {
  const formData = new FormData();
  formData.append('file', file);
  appendJsonRequestPart(formData, {
    targets,
    dataPreserve: binaryOption(optionalParams.data_preserve),
    configurationPreserve: binaryOption(optionalParams.cfg_preserve),
    run: binaryOption(optionalParams.run),
    comment: optionalParams.comment ?? '',
  });

  try {
    const response = await apiClient.post(
      API_ENDPOINTS.ACTIONS.LAUNCH_DEPLOY,
      formData,
      {
        headers: atomicHeaders(optionalParams.idempotencyKey),
        timeout: API_CONFIG.UPLOAD_TIMEOUT,
        onUploadProgress: (event) => {
          if (!optionalParams.onUploadProgress || !event.total) return;
          optionalParams.onUploadProgress(Math.min(100, Math.round((event.loaded / event.total) * 100)));
        },
      }
    );
    return response.data;
  } catch (error: any) {
    handleAxiosError(error);
    throw error;
  }
}

export async function launchUploadLicense(
  file: File,
  targets: ActionTargetReference[],
  optionalParams: AtomicLaunchOptions & { onUploadProgress?: (percent: number) => void }
): Promise<Action> {
  const formData = new FormData();
  formData.append('file', file);
  appendJsonRequestPart(formData, {
    targets,
    comment: optionalParams.comment ?? '',
  });

  try {
    const response = await apiClient.post(
      API_ENDPOINTS.ACTIONS.LAUNCH_LICENSE_UPDATE,
      formData,
      {
        headers: atomicHeaders(optionalParams.idempotencyKey),
        timeout: API_CONFIG.UPLOAD_TIMEOUT,
        onUploadProgress: (event) => {
          if (!optionalParams.onUploadProgress || !event.total) return;
          optionalParams.onUploadProgress(Math.min(100, Math.round((event.loaded / event.total) * 100)));
        },
      }
    );
    return response.data;
  } catch (error: any) {
    handleAxiosError(error);
    throw error;
  }
}

export async function uploadDeploy(
  file: File,
  actionId: number,
  optionalParams: { [key: string]: string | number } = {}
): Promise<Action> {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("actionId", actionId.toString());

  try {
    const response = await apiClient.post(
      API_ENDPOINTS.ACTIONS.START_DEPLOY,
      formData,
      {
        params: { ...optionalParams },
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    return response.data;
  } catch (error: any) {
    handleAxiosError(error);
    throw error;
  }
}

export async function uploadConfig(
  file: File,
  actionId: number,
  optionalParams: { [key: string]: string | number } = {}
): Promise<Action> {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("actionId", actionId.toString());

  try {
    const response = await apiClient.post(
      API_ENDPOINTS.ACTIONS.START_CONFIG_UPDATE,
      formData,
      {
        params: { ...optionalParams },
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    return response.data;
  } catch (error: any) {
    handleAxiosError(error);
    throw error;
  }
}

export async function bulkUploadDeployment(
  file: File,
  actionId: number,
  optionalParams: { [key: string]: string | number } = {}
): Promise<Action> {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("actionId", actionId.toString());

  try {
    const response = await apiClient.post(
      API_ENDPOINTS.ACTIONS.START_DEPLOY,
      formData,
      {
        params: { ...optionalParams },
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    return response.data;
  } catch (error: any) {
    handleAxiosError(error);
    throw error;
  }
}

export async function bulkUploadConfig(
  file: File,
  actionId: number,
  optionalParams: { [key: string]: string | number } = {}
): Promise<Action> {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("actionId", actionId.toString());

  try {
    const response = await apiClient.post(
      API_ENDPOINTS.ACTIONS.START_CONFIG_UPDATE,
      formData,
      {
        params: { ...optionalParams },
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    return response.data;
  } catch (error: any) {
    handleAxiosError(error);
    throw error;
  }
}

export async function bulkRebootRtus(
  actionId: number,
  optionalParams: { [key: string]: string | number } = {}
): Promise<Action> {
  try {
    const response = await apiClient.post(
      API_ENDPOINTS.ACTIONS.START_REBOOT,
      null,
      {
        params: { actionId, ...optionalParams },
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    return response.data;
  } catch (error: any) {
    handleAxiosError(error);
    throw error;
  }
}

export const pingRTU = async (ip: string | null) => {
  return pingRtu(ip);
};

export async function probeRtuById(rtuId: number | string): Promise<RtuProbeResponseDto> {
  if (rtuId === undefined || rtuId === null || String(rtuId).trim() === '') {
    throw new Error('RTU id is required');
  }

  try {
    const response = await apiClient.post<RtuProbeResponseDto>(API_ENDPOINTS.RTU.PROBE_BY_ID(rtuId));
    return response.data;
  } catch (error: any) {
    handleAxiosError(error);
    throw error;
  }
}

export async function runRtuPing(
  rtuId: number | string,
  request: RtuPingRunRequestDto = {}
): Promise<RtuPingRunResponseDto> {
  if (rtuId === undefined || rtuId === null || String(rtuId).trim() === '') {
    throw new Error('RTU id is required');
  }

  try {
    const response = await apiClient.post<RtuPingRunResponseDto>(
      API_ENDPOINTS.RTU.PING_RUN_BY_ID(rtuId),
      request
    );
    return response.data;
  } catch (error: any) {
    handleAxiosError(error);
    throw error;
  }
}

export async function runBulkRtuPing(
  request: RtuBulkPingRunRequestDto
): Promise<RtuBulkPingRunResponseDto> {
  try {
    const response = await apiClient.post<RtuBulkPingRunResponseDto>(
      API_ENDPOINTS.RTU.BULK_PING_RUN,
      request
    );
    return response.data;
  } catch (error: any) {
    handleAxiosError(error);
    throw error;
  }
}

export async function startRtuPingBatch(
  request: RtuBulkPingRunRequestDto
): Promise<RtuPingBatchSnapshotDto> {
  try {
    const response = await apiClient.post<RtuPingBatchSnapshotDto>(
      API_ENDPOINTS.RTU.PING_BATCHES,
      request
    );
    return response.data;
  } catch (error: any) {
    handleAxiosError(error);
    throw error;
  }
}

export async function fetchRtuPingBatchSnapshot(
  batchRunId: string
): Promise<RtuPingBatchSnapshotDto> {
  try {
    const response = await apiClient.get<RtuPingBatchSnapshotDto>(
      API_ENDPOINTS.RTU.PING_BATCH_BY_ID(batchRunId)
    );
    return response.data;
  } catch (error: any) {
    handleAxiosError(error);
    throw error;
  }
}

export interface RtuActionHint {
  canPing: boolean;
  canExecuteActions: boolean;
}

/**
 * Batched per-target ping/Action-execute presentation hints, so a mixed-group
 * user (e.g. Administrator on one Well, Operator only on another) sees the
 * right enabled/disabled state per RTU instead of one global capability flag.
 * Presentation only - the backend re-checks authoritatively on every actual
 * ping/Action request regardless of this response.
 *
 * Concurrent callers in the same turn are coalesced onto one GET of the
 * existing /rtu/action-hints?ids= batch endpoint. Identical in-flight ID sets
 * for the same scope share that Promise. The optional scope (the caller's
 * identity key) keeps a stale identity's in-flight request or microtask batch
 * from being joined by a different identity requesting the same RTU ids -
 * see fetchRtuActionHints. Settled results are not cached (backend
 * Cache-Control is no-store; revoked access must not be replayed).
 *
 * Maximum staleness of a displayed hint (the caller, RTUActions, revalidates
 * on useCapabilityAccess's accessIdentityKey/accessEvidenceVersion - see
 * there for how each mode advances that version):
 *  - Backend-capability-source mode: the current-user refresh interval
 *    (nominal ~25s - CURRENT_USER_ACCESS_TTL_MS 30s minus
 *    CURRENT_USER_ACCESS_PROACTIVE_LEAD_MS 5s in currentUserAccessService.ts
 *    - plus an immediate revalidation on tab focus/visibility resume) + this
 *    request's own duration (bounded by the 30s axios timeout,
 *    API_CONFIG.TIMEOUT; a still-in-flight identical request can be joined
 *    instead of re-queried, so the worst case adds one more refresh interval)
 *    + backend-side permission caching for action hints, which is NONE: the
 *    /rtu/action-hints response is Cache-Control: no-store (RtuController.
 *    getActionHints), and AccessControlService.resolveActionHints ->
 *    filterPingableAuthoritative / filterWritableAuthoritative are both
 *    documented "Always fresh" and bypass the class's own short-TTL display
 *    cache (AccessControlService.java, class javadoc + the two methods' own
 *    javadoc); the capability check they fall back to,
 *    hasCapabilityAuthoritative -> getCapabilityStatesForEmployee, queries
 *    dbo.DMS_security_capability_grant directly on every call
 *    (JdbcUserCapabilityService.getCapabilityStatesForEmployee) rather than
 *    going through that same class's separate ~5-minute capabilities cache
 *    (getCapabilitiesForEmployee, a different method).
 *  - Keycloak-token-roles mode and local-auth mode (no backend evidence
 *    cycle): the shared accessRevalidationClock's period (30s,
 *    CURRENT_USER_ACCESS_TTL_MS) + this request's own duration (same 30s
 *    axios-timeout bound, with the same in-flight-join caveat) - see
 *    accessRevalidationClock.ts and useCapabilityAccess's accessEvidenceVersion.
 */
type ActionHintWaiter = {
  scopeKey: string;
  ids: number[];
  resolve: (hints: Record<number, RtuActionHint>) => void;
  reject: (error: unknown) => void;
};

const actionHintsInFlight = createKeyedInFlightShare<Record<number, RtuActionHint>>();
let pendingActionHintWaiters: ActionHintWaiter[] = [];
const actionHintFlushScheduledScopes = new Set<string>();
let actionHintLogoutListenerBound = false;

const actionHintIdsKey = (ids: number[]) => [...ids].sort((a, b) => a - b).join(',');
const actionHintShareKey = (scopeKey: string, ids: number[]) => `${scopeKey}|${actionHintIdsKey(ids)}`;

const requestActionHintsFromApi = async (
  uniqueIds: number[]
): Promise<Record<number, RtuActionHint>> => {
  try {
    const response = await apiClient.get<Record<number, RtuActionHint>>(
      API_ENDPOINTS.RTU.ACTION_HINTS,
      { params: { ids: uniqueIds.join(',') } }
    );
    return response.data ?? {};
  } catch (error: any) {
    handleAxiosError(error);
    throw error;
  }
};

const flushActionHintBatch = async (scopeKey: string) => {
  const waiters = pendingActionHintWaiters.filter((waiter) => waiter.scopeKey === scopeKey);
  pendingActionHintWaiters = pendingActionHintWaiters.filter((waiter) => waiter.scopeKey !== scopeKey);
  if (waiters.length === 0) {
    return;
  }

  const allIds = Array.from(new Set(waiters.flatMap((waiter) => waiter.ids)));
  try {
    const hints = await requestActionHintsFromApi(allIds);
    waiters.forEach((waiter) => waiter.resolve(hints));
  } catch (error) {
    waiters.forEach((waiter) => waiter.reject(error));
  }
};

export const resetRtuActionHintSharing = () => {
  // Settle - never silently drop - any waiter still queued for a microtask
  // flush that hasn't fired yet (see flushActionHintBatch, which removes its
  // scope's waiters from this array before the HTTP request is dispatched):
  // an unsettled Promise here would leave RTUActions' fetchRtuActionHints
  // caller awaiting forever. This does not touch a request already dispatched
  // to axios - that settles on its own via the response or the 30s timeout.
  const waitersToReject = pendingActionHintWaiters;
  pendingActionHintWaiters = [];
  actionHintFlushScheduledScopes.clear();
  actionHintsInFlight.reset();
  waitersToReject.forEach((waiter) => waiter.reject(
    new Error('action-hints request superseded by a session change')
  ));
};

// Same pattern as currentUserAccessService.bindLogoutInvalidation: a logout
// must drop any sharing keyed to the outgoing identity so the next identity
// never joins a leftover in-flight request or batch.
const bindActionHintLogoutInvalidation = () => {
  if (actionHintLogoutListenerBound || typeof window === 'undefined') {
    return;
  }
  actionHintLogoutListenerBound = true;
  window.addEventListener('dms-auth-logout', () => {
    resetRtuActionHintSharing();
  });
};
bindActionHintLogoutInvalidation();

export async function fetchRtuActionHints(
  rtuIds: number[],
  scopeKey: string = ''
): Promise<Record<number, RtuActionHint>> {
  const uniqueIds = Array.from(new Set(rtuIds.filter((id) => Number.isInteger(id) && id > 0)));
  if (uniqueIds.length === 0) {
    return {};
  }

  return actionHintsInFlight.run(actionHintShareKey(scopeKey, uniqueIds), () => (
    new Promise<Record<number, RtuActionHint>>((resolve, reject) => {
      pendingActionHintWaiters.push({ scopeKey, ids: uniqueIds, resolve, reject });
      if (!actionHintFlushScheduledScopes.has(scopeKey)) {
        actionHintFlushScheduledScopes.add(scopeKey);
        queueMicrotask(() => {
          actionHintFlushScheduledScopes.delete(scopeKey);
          void flushActionHintBatch(scopeKey);
        });
      }
    })
  ));
}

export async function fetchLatestRtuReachability(
  rtuId: number | string
): Promise<RtuPingRunResponseDto | null> {
  try {
    const response = await apiClient.get<RtuPingRunResponseDto>(
      API_ENDPOINTS.RTU.LATEST_REACHABILITY_BY_ID(rtuId)
    );
    return response.data;
  } catch (error: any) {
    if (error?.response?.status === 404) {
      return null;
    }
    handleAxiosError(error);
    throw error;
  }
}

export async function pingRtu(ip: string | null) {
  try {
    if (!ip) {
      throw new Error("IP address is required");
    }

    const response = await apiClient.post(
      API_ENDPOINTS.UTILITY.PING,
      { host: ip },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    return response.data;
  } catch (error: any) {
    handleAxiosError(error);
    throw error;
  }
}

export async function fetchRTUsInfo(rtuEndpoints: any[]) {
  try {
    const response = await apiClient.post(
      API_ENDPOINTS.RTU.FETCH,
      rtuEndpoints
    );
    return response.data;
  } catch (error: any) {
    handleAxiosError(error);
    throw error;
  }
}

export async function getRtuById(rtuId: number) {
  try {
    const response = await apiClient.get(
      API_ENDPOINTS.RTU.GET_BY_ID(rtuId)
    );
    return response.data;
  } catch (error: any) {
    handleAxiosError(error);
    throw error;
  }
}

/**
 * Wells and cluster of one RTU record (GET /api/rtu/{rtuId}/cluster-details). Absent arrays are empty; nothing is
 * invented. Errors are rethrown without logging the response: the details page simply omits the section.
 */
export async function getRtuClusterDetails(rtuId: number | string): Promise<RtuClusterDetails> {
  const asArray = <T,>(value: unknown): T[] => (Array.isArray(value) ? (value as T[]) : []);
  const response = await apiClient.get(`/rtu/${encodeURIComponent(String(rtuId))}/cluster-details`);
  const data = (response?.data ?? {}) as Partial<RtuClusterDetails> & { currentWells?: unknown };
  return {
    rtuId: typeof data.rtuId === 'number' ? data.rtuId : Number(rtuId),
    assetId: data.assetId ?? null,
    recordState: data.recordState ?? null,
    wellListState: data.wellListState ?? null,
    currentWells: asArray(data.currentWells),
    currentWellsSource: data.currentWellsSource ?? null,
    parent: data.parent ?? null,
    children: asArray(data.children),
    formerWells: asArray(data.formerWells),
    wellHistory: asArray(data.wellHistory),
    clusterHistory: asArray(data.clusterHistory),
    truncated: data.truncated === true,
  };
}

export interface InstantSystemEventHistoryQuery {
  page?: number;
  size?: number;
}

const withInstantEventHistoryPageFallback = (
  data: unknown,
  fallbackPage = 0,
  fallbackSize = 25
): PageResponseDto<InstantSystemEvent> => {
  if (Array.isArray(data)) {
    return {
      content: data as InstantSystemEvent[],
      page: fallbackPage,
      size: fallbackSize || data.length,
      totalElements: data.length,
      totalPages: data.length > 0 ? 1 : 0,
    };
  }

  const typed = data as Partial<PageResponseDto<InstantSystemEvent>> | null | undefined;
  return {
    content: typed?.content ?? [],
    page: typed?.page ?? fallbackPage,
    size: typed?.size ?? fallbackSize,
    totalElements: typed?.totalElements ?? typed?.content?.length ?? 0,
    totalPages: typed?.totalPages ?? 0,
  };
};

export async function getRtuInstantSystemEventHistory(
  rtuId: number | string,
  query: InstantSystemEventHistoryQuery = {}
): Promise<PageResponseDto<InstantSystemEvent>> {
  try {
    const response = await apiClient.get(
      API_ENDPOINTS.RTU.INSTANT_SYSTEM_EVENT_HISTORY(rtuId),
      {
        params: {
          page: query.page,
          size: query.size,
        },
      }
    );
    return withInstantEventHistoryPageFallback(response.data, query.page ?? 0, query.size ?? 25);
  } catch (error: any) {
    handleAxiosError(error);
    throw error;
  }
}

export async function deleteAllRtus() {
  try {
    const response = await apiClient.delete(
      API_ENDPOINTS.RTU.DELETE_ALL
    );
    return response.data;
  } catch (error: any) {
    handleAxiosError(error);
    throw error;
  }
}

export async function getAllRtusData() {
  try {
    const response = await apiClient.get(
      API_ENDPOINTS.RTU.GET_ALL_DATA
    );
    return response.data;
  } catch (error: any) {
    handleAxiosError(error);
    throw error;
  }
}

export async function downloadDb1File() {
  try {
    const response = await apiClient.get(
      API_ENDPOINTS.RTU.DOWNLOAD_DB1,
      {
        responseType: 'blob',
      }
    );
    return response.data;
  } catch (error: any) {
    handleAxiosError(error);
    throw error;
  }
}

function handleAxiosError(error: any) {
  if (error.response) {
    // The request was made and the server responded with a status code
    // that falls out of the range of 2xx
    console.error('Response error:', error.response.data);
    console.error('Response status:', error.response.status);
    console.error('Response headers:', error.response.headers);
  } else if (error.request) {
    // The request was made but no response was received
    console.error('Request error:', error.request);
  } else {
    // Something happened in setting up the request that triggered an Error
    console.error('Error:', error.message);
  }
}
